/**
 * Stable error codes (docs/api-conventions.md#fouten). The code is the contract; the title is the
 * English default, translated by the API through Accept-Language. Add a code here before using it.
 */
export const ERROR_CODES = {
  bad_request: { status: 400, title: 'Bad request' },
  malformed_json: { status: 400, title: 'Malformed JSON body' },
  invalid_id: { status: 400, title: 'Invalid identifier' },
  invalid_cursor: { status: 400, title: 'Invalid cursor' },
  unauthorized: { status: 401, title: 'Authentication required' },
  invalid_api_key: { status: 401, title: 'Invalid API key' },
  insufficient_scope: { status: 403, title: 'Insufficient scope' },
  live_not_enabled: { status: 403, title: 'Live mode is not enabled for this organisation' },
  test_mode_only: { status: 403, title: 'Only available in test mode' },
  turnstile_failed: { status: 403, title: 'Turnstile verification failed' },
  /**
   * The session is real but has not shown a second factor yet, or the account has none and the
   * organisation demands one (M4-12). Its own code rather than a `forbidden` with a sentence in
   * the detail, because the console has to tell the two states apart to know whether to show the
   * code prompt or the enrolment screen, and reading that out of a detail string is how a
   * translation ends up deciding where a user lands.
   */
  two_factor_required: { status: 403, title: 'Two-factor authentication is required' },
  forbidden: { status: 403, title: 'Forbidden' },
  not_found: { status: 404, title: 'Not found' },
  pod_not_available: { status: 404, title: 'The proof of delivery is not available yet' },
  invoice_not_available: { status: 404, title: 'The invoice is not available yet' },
  invalid_transition: { status: 409, title: 'Invalid state transition' },
  invalid_state: { status: 409, title: 'The shipment is in the wrong state for this operation' },
  field_frozen: { status: 409, title: 'Field is frozen in the current status' },
  shipment_immutable: { status: 409, title: 'The shipment can no longer be changed' },
  already_signed: { status: 409, title: 'Already signed' },
  idempotency_key_reused: { status: 409, title: 'Idempotency key reused with a different request' },
  idempotency_in_progress: {
    status: 409,
    title: 'A request with this idempotency key is still running',
  },
  conflict: { status: 409, title: 'Conflict' },
  link_expired: { status: 410, title: 'Signed link expired' },
  token_expired: { status: 410, title: 'The link has expired' },
  token_rotated: { status: 410, title: 'The link was replaced by a newer one' },
  token_revoked: { status: 410, title: 'The link was withdrawn' },
  payload_too_large: { status: 413, title: 'Payload too large' },
  unsupported_media_type: { status: 415, title: 'Unsupported media type' },
  validation_failed: { status: 422, title: 'Validation failed' },
  subset_unknown: { status: 422, title: 'Unknown eFTI subset' },
  invoice_incomplete: { status: 422, title: 'The invoice misses something EN 16931 requires' },
  otp_invalid: { status: 422, title: 'The one-time password is wrong or expired' },
  /**
   * The sign link asks for `ades` or `qes`, so the signer has to identify themselves at the
   * trust service provider first (M5-02). Its own code because the sign PWA turns it into a step
   * rather than into an error screen: nothing is wrong, something is still to be done.
   */
  identification_required: {
    status: 422,
    title: 'This signature needs an identification at the trust service provider',
  },
  /** The `identification_token` is unknown, already used or expired. */
  identification_invalid: {
    status: 422,
    title: 'The identification is unknown, already used or expired',
  },
  /**
   * The six digits, or the recovery code, did not match. The same code for "wrong", "expired" and
   * "already used", on purpose: telling a caller which of the three it was tells an attacker
   * whether they guessed a live code, and the audit row records the difference where it belongs.
   */
  two_factor_invalid: {
    status: 422,
    title: 'The two-factor code is wrong, expired or already used',
  },
  rate_limited: { status: 429, title: 'Too many requests' },
  otp_locked: { status: 429, title: 'Too many wrong one-time passwords' },
  internal_error: { status: 500, title: 'Internal error' },
  pdf_render_failed: { status: 500, title: 'The consignment note PDF could not be rendered' },
  not_implemented: { status: 501, title: 'Not implemented' },
  provider_unavailable: { status: 502, title: 'The eCMR provider is unavailable' },
  /** The qualified trust service provider refused or could not be reached (M5-02). */
  qtsp_unavailable: { status: 503, title: 'The trust service provider is unavailable' },
  billing_not_configured: { status: 503, title: 'Billing is not configured for this organisation' },
  service_unavailable: { status: 503, title: 'Service unavailable' },
} as const satisfies Record<string, { status: number; title: string }>

export type ErrorCode = keyof typeof ERROR_CODES

/** Problem `type` URIs resolve to the public error documentation. */
export const ERROR_TYPE_BASE = 'https://cargofollow.com/errors/'

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && Object.hasOwn(ERROR_CODES, value)
}
